'use client';
// components/chatbot/useProactiveTrigger.ts
// Decides WHEN to proactively surface the chat note, based on real behaviour
// rather than a blind timer:
//   • dwell  — visitor lingers on a page (faster on high-intent pages)
//   • exit   — desktop visitor moves the cursor to leave (exit intent)
// Fires at most once per page-view session; pure client-side, no network.
import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

export type ProactiveTrigger = 'dwell' | 'exit' | null;

// Pages where a visitor is closer to converting — engage sooner.
const HIGH_INTENT = ['/services', '/contact', '/projects', '/work'];

const HIGH_INTENT_DWELL_MS = 20_000;
const DEFAULT_DWELL_MS      = 45_000;

export function useProactiveTrigger(enabled: boolean): { trigger: ProactiveTrigger; pathname: string } {
    const pathname = usePathname() || '/';
    const [trigger, setTrigger] = useState<ProactiveTrigger>(null);
    const firedRef = useRef(false);

    useEffect(() => {
        if (!enabled || firedRef.current) return;

        const fire = (t: Exclude<ProactiveTrigger, null>) => {
            if (firedRef.current) return;
            firedRef.current = true;
            setTrigger(t);
        };

        // Dwell timer — shorter on high-intent pages.
        const isHighIntent = HIGH_INTENT.some((p) => pathname.includes(p));
        const dwellId = window.setTimeout(() => fire('dwell'), isHighIntent ? HIGH_INTENT_DWELL_MS : DEFAULT_DWELL_MS);

        // Exit intent — cursor leaves through the top of the viewport (desktop only).
        const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        const onMouseOut = (e: MouseEvent) => {
            if (e.clientY <= 0 && !e.relatedTarget) fire('exit');
        };
        if (isDesktop) document.addEventListener('mouseout', onMouseOut);

        return () => {
            window.clearTimeout(dwellId);
            document.removeEventListener('mouseout', onMouseOut);
        };
    }, [pathname, enabled]);

    return { trigger, pathname };
}
